import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Newspaper } from 'lucide-react';
import { getNewsBySlug, NEWS_STATUS } from '../../../database/news';
import NewsRenderer from '../../News/NewsRenderer';
import NewsComments from './Comments/NewsComments';
import SEO from '../../SEO';
import { absoluteUrl, stripHtml, truncate } from '../../../lib/seo';

const toIsoDate = (v) => {
  if (!v) return undefined;
  if (typeof v.toDate === 'function') return v.toDate().toISOString();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

const fmt = (ts) => {
  if (!ts) return '';
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  });
};

const NewsArticle = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getNewsBySlug(slug);
        if (!cancelled) setPost(data);
      } catch {
        if (!cancelled) setPost(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-screen-md px-4 pb-24 pt-12 sm:px-6">
        <div className="h-8 w-32 animate-pulse rounded bg-muted/10" />
        <div className="mt-6 h-14 w-3/4 animate-pulse rounded bg-muted/10" />
        <div className="mt-10 h-64 animate-pulse rounded bg-muted/10" />
      </div>
    );
  }

  if (!post || post.status !== NEWS_STATUS.PUBLISHED) {
    return (
      <div className="mx-auto w-full max-w-screen-md px-4 pb-24 pt-20 text-center sm:px-6">
        <SEO title="Post not found" noindex canonicalPath={`/news/${slug}`} />
        <Newspaper className="mx-auto h-10 w-10 text-muted-foreground/50" />
        <h1 className="mt-4 font-display text-3xl font-bold">Post not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This article may have been unpublished or doesn't exist.
        </p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-heat hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to news
        </Link>
      </div>
    );
  }

  const firstText = (post.blocks || []).find((b) => b.type === 'text')?.html || '';
  const articleDesc = truncate(stripHtml(firstText) || `${post.title} — KZCOMP news`, 160);
  const publishedIso = toIsoDate(post.publishedAt);
  const modifiedIso = toIsoDate(post.updatedAt);
  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: post.title,
    description: articleDesc,
    url: absoluteUrl(`/news/${post.slug || slug}`),
    mainEntityOfPage: absoluteUrl(`/news/${post.slug || slug}`),
    ...(publishedIso ? { datePublished: publishedIso } : {}),
    ...(modifiedIso ? { dateModified: modifiedIso } : {}),
    ...(post.coverImage?.url ? { image: post.coverImage.url } : {}),
    publisher: {
      '@type': 'Organization',
      name: 'KZCOMP',
      logo: { '@type': 'ImageObject', url: absoluteUrl('/logo512.png') },
    },
    author: { '@type': 'Organization', name: 'KZCOMP' },
  };

  return (
    <article className="mx-auto w-full max-w-screen-md px-4 pb-24 pt-12 sm:px-6">
      <SEO
        title={post.title}
        description={articleDesc}
        type="article"
        canonicalPath={`/news/${post.slug || slug}`}
        image={post.coverImage?.url}
        publishedTime={publishedIso}
        modifiedTime={modifiedIso}
        jsonLd={articleLd}
      />
      <Link
        to="/news"
        className="mb-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        All news
      </Link>

      <div className="mb-4 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
        <span className="h-px w-6 bg-heat" />
        {fmt(post.publishedAt)}
      </div>

      <h1 className="font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
        {post.title}
      </h1>

      {post.coverImage?.url && (
        <img
          src={post.coverImage.url}
          alt=""
          className="mt-10 w-full rounded-md border border-border/60 object-cover"
        />
      )}

      <div className="mt-10">
        <NewsRenderer blocks={post.blocks || []} />
      </div>

      <NewsComments newsId={post.id} />
    </article>
  );
};

export default NewsArticle;
